import { ElementType, forwardRef } from 'react';
import { cx } from '@linaria/atomic';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../type-utils/polymorphic';
import { Chip, type ChipProps } from '../chip/chip.component';
import { useSwitchChipGroupContext } from './switch-chip-group-context';
import {
  switchChipBaseStyle,
  switchChipSelectedBaseStyle,
  switchChipSelectedDisabledStyle,
  switchChipSelectedEnabledStyle,
  switchChipUnselectedBaseStyle,
  switchChipUnselectedDisabledStyle,
  switchChipUnselectedEnabledStyle,
  switchChipNewBaseStyle,
  switchChipNewSelectedStyle,
  switchChipNewUnselectedStyle,
  switchChipNewDisabledSelectedStyle,
  switchChipNewDisabledUnselectedStyle,
} from './switch-chip.styles';

export type SwitchChipProps = Omit<ChipProps, 'onClick'> & {
  value: string;
  disabled?: boolean;
  isNew?: boolean;
};

type PolymorphicSwitchChipProps<T extends ElementType> = PolymorphicComponentPropWithRef<T, SwitchChipProps>;

type PolymorphicSwitchChipType = (<C extends ElementType = 'div'>(
  props: PolymorphicSwitchChipProps<C>
) => React.ReactNode) & {
  displayName?: string;
};

export const SwitchChip: PolymorphicSwitchChipType = forwardRef(
  <T extends ElementType = 'div'>(
    { as, value, disabled, isNew, className, ...props }: PolymorphicSwitchChipProps<T>,
    ref: PolymorphicRef<T>
  ) => {
    const { value: contextValue, toggleValue, disabled: contextDisabled } = useSwitchChipGroupContext();
    const resolvedDisabled = contextDisabled || disabled;
    const selected = contextValue.includes(value);
    const Component = as || 'div';

    const chipClassName = isNew
      ? cx(
          switchChipNewBaseStyle,
          selected ? switchChipNewSelectedStyle : switchChipNewUnselectedStyle,
          resolvedDisabled && selected && switchChipNewDisabledSelectedStyle,
          resolvedDisabled && !selected && switchChipNewDisabledUnselectedStyle
        )
      : cx(
          switchChipBaseStyle,
          selected ? switchChipSelectedBaseStyle : switchChipUnselectedBaseStyle,
          selected
            ? resolvedDisabled
              ? switchChipSelectedDisabledStyle
              : switchChipSelectedEnabledStyle
            : resolvedDisabled
            ? switchChipUnselectedDisabledStyle
            : switchChipUnselectedEnabledStyle
        );

    return (
      <Chip
        as={Component}
        className={cx(chipClassName, className)}
        tabIndex={resolvedDisabled ? -1 : 0}
        size='large'
        {...(props as any)}
        onClick={
          resolvedDisabled
            ? undefined
            : (e) => {
                e.stopPropagation();
                toggleValue(value);
              }
        }
        onKeyDown={
          resolvedDisabled
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === 'Space') {
                  e.stopPropagation();
                  toggleValue(value);
                }
              }
        }
        variant='outline'
        ref={ref}
      />
    );
  }
);

SwitchChip.displayName = 'SwitchChip';
